import { Injectable, isDevMode } from '@angular/core'
import { Observable, BehaviorSubject, of } from 'rxjs'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { User } from '../../models/user'
import { UrlCollection } from '../../url-collection'
import { switchMap, map, tap, catchError, take, distinctUntilChanged, mergeMap } from 'rxjs/operators'
import { HttpHeadersService } from '../../services/http-headers.service'
import { HttpResult } from '../../models/http/http-result'
import { HttpOptionsFactory } from '../../models/http/http-options-factory'
import { Store, select } from '@ngrx/store'
import { RootStoreState } from '../../root-store/root-index'
import * as fromUserSelectors from '../../root-store/user-profile-store/user-profile.selectors'
import { JmeleonActionsPermissionService } from '../../modules/permissions'
import { TokenValidationResult } from '../models/token-validation-result.model'
import moment from 'moment'
import { UrlAsObservableService } from '../../app-config/url-as-observable.service'
import { Md5 } from 'ts-md5'

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private static clientSessionId = Md5.hashStr('' + Date.now() + Math.random())

  public static getClientSessionId(): string {
    return AuthService.clientSessionId
  }

  // ---ATTENTION---
  // use only if use of the observables is not possible!!!
  public localUser: User
  public isLoggedIn = false
  // ---------------

  public redirectUrl: string

  public localUser$: Observable<User>
  public isLoggedIn$: Observable<boolean>
  public tokenIsValidated$: Observable<boolean>
  public enforcePasswdChange$: Observable<boolean>

  private $_hasActuallyLoggedIn = new BehaviorSubject<boolean>(false)
  /** Determines, whether the user has actually logged in in this session (as opposed to refreshing the session with F5 and restoring the session from store or opening the app in a new tab). */
  public hasActuallyLoggedIn$ = this.$_hasActuallyLoggedIn.asObservable().pipe(distinctUntilChanged())

  constructor(
    private http: HttpClient,
    private httpHeaderService: HttpHeadersService,
    private store$: Store<RootStoreState.State>,
    private jmlActionPermissionsService: JmeleonActionsPermissionService,
    private urlService: UrlAsObservableService
  ) {
    this.localUser$ = this.store$.pipe(
      select(fromUserSelectors.selectUserProfileUser)
      // tap(user => console.log('this.localUser$:', user))
    )
    this.isLoggedIn$ = this.store$.pipe(
      select(fromUserSelectors.selectUserProfileUser),
      map((user) => !!user && !!user.token)
    )

    this.tokenIsValidated$ = this.store$.pipe(select(fromUserSelectors.selectTokenIsValidated))

    this.enforcePasswdChange$ = this.store$.pipe(select(fromUserSelectors.selectEnforcePwChange))

    this.localUser$.subscribe((user) => {
      this.localUser = user

      if (!!user && !!user.allowedActions) {
        this.jmlActionPermissionsService.initWithCurrentUserActionsIfNotYetInitialized()
      }
    })
    this.isLoggedIn$.subscribe((isLoggedIn) => {
      // console.log('is logged in: ', isLoggedIn);
      this.isLoggedIn = isLoggedIn
    })
  }

  login(userName: string, pass: string): Observable<HttpResult<User>> {
    const result = this.http
      .post<User>(
        UrlCollection.UserManagement.LOGIN(),
        { user: userName, passwd: pass, userAgent: navigator.userAgent },
        new HttpOptionsFactory().addContentTypeJson().buildWithObserveOption()
      )
      .pipe(
        map((httpResponse) => {
          return httpResponse.body as User
        }),
        tap((user) => {
          this.$_hasActuallyLoggedIn.next(true)
        }),
        map((user) => {
          const httpResult: HttpResult<User> = {
            success: true,
            result: user
          }
          return httpResult
        }),
        catchError((err: HttpErrorResponse) => {
          console.log('error: ', err)
          let httpResult: HttpResult<User>
          // wrong credentials
          if (err.status === 403) {
            httpResult = {
              success: false,
              errMsg: err.error.message
            }
            // no vpn connection, ...?
          } else {
            httpResult = {
              success: false,
              errMsg: err.statusText
            }
          }
          return of(httpResult)
        })
      )
    return result
  }

  logout(sendLogoutRequestToServer = true): Observable<[boolean, string]> {
    this.jmlActionPermissionsService.flushPermissions()
    // for invalid token
    if (!sendLogoutRequestToServer) {
      this.$_hasActuallyLoggedIn.next(false)
      return of([true, ''])
    }

    const logoutResult = this.http.get(UrlCollection.UserManagement.LOGOUT()).pipe(
      map(() => {
        this.$_hasActuallyLoggedIn.next(false)
        const result: [boolean, string] = [true, '']
        return result
      }),
      catchError((err) => {
        // console.log('logout error: ', err);
        const result: [boolean, string] = [false, err['error']['message'] ? err['error']['message'].toString() : err]
        this.$_hasActuallyLoggedIn.next(false)
        return of(result)
      })
    )
    return logoutResult
  }

  // test!!!
  changePassword(oldpass: string, newpass: string): Observable<boolean> {
    return this.http
      .post(
        UrlCollection.UserManagement.CHANGE_PASSWD(),
        { oldpasswd: oldpass, newpasswd: newpass },
        new HttpOptionsFactory().addContentTypeJson().buildWithObserveOption()
      )
      .pipe(
        map(() => true),
        catchError(() => of(false))
      )

    // return this.localUser$
    //   .pipe(
    //     switchMap(user => {
    //       const httpOptions = new HttpOptions().addContentTypeJson().addObserveOption();
    //       // const httpOptions = this.httpHeaderService.getHttpOptions(
    //       //   this.httpHeaderService.addContentTypeJson(null), true);
    //       // {
    //       //   headers: new HttpHeaders({
    //       //     'Content-Type':  'application/json',
    //       //     'authorization': user.token
    //       //   }),
    //       //   observe: 'response' as 'response'
    //       // };

    //       return this.http
    //         .post(UrlCollection.UserManagement.CHANGE_PASSWD(),
    //           JSON.stringify({oldpasswd: oldpass, newpasswd: newpass}), httpOptions)
    //         .pipe(
    //           map(() => true),
    //           catchError(() => of(false))
    //         );
    //     })
    //   );
  }

  validateToken(): Observable<TokenValidationResult> {
    const result = this.localUser$.pipe(
      take(1),
      switchMap((user) => {
        console.log('token-validation. token: ', user.token)
        if (!user || !user.token) {
          const failureResult = new TokenValidationResult()
          failureResult.tokenIsValid = false
          return of(failureResult)
        }
        return this.urlService.UserManagement.VALIDATE_TOKEN$(user.token).pipe(
          switchMap((url) => this.http.get<TokenValidationResult>(url)),
          map((valResult) => {
            // avoid comparing time when validUntil property is not set.
            if (!valResult.loginContext?.validUntil) {
              valResult.tokenIsValid = false
              return valResult
            }
            if (moment().isAfter(moment(valResult.loginContext.validUntil))) {
              console.log('valResult.loginContext.validUntil', valResult.loginContext.validUntil)
              valResult.tokenIsValid = false
            }
            // sorting is done within the service, because this will not be called by an effect but directly by a guard.
            else if (
              isDevMode() &&
              !!valResult &&
              !!valResult.loginContext &&
              !!valResult.loginContext.allowedActions
            ) {
              valResult.loginContext.allowedActions = valResult.loginContext.allowedActions.sort()
            }
            return valResult
          })
        )
      }),
      tap((valResult) => console.log('token validation result: ', valResult))
    )
    return result
  }

  prolongToken(): Observable<TokenValidationResult> {
    const result = this.localUser$.pipe(
      take(1),
      switchMap((user) => {
        console.log('token-prolonging. token: ', user.token)
        if (!user || !user.token) {
          const failureResult = new TokenValidationResult()
          failureResult.tokenIsValid = false
          return of(failureResult)
        }
        const url = UrlCollection.UserManagement.PROLONG_TOKEN()
        return this.http.get<TokenValidationResult>(url).pipe(
          map((valResult) => {
            // avoid comparing time when validUntil property is not set.
            if (!valResult.loginContext?.validUntil) {
              valResult.tokenIsValid = false
              return valResult
            }
            if (moment().isAfter(moment(valResult.loginContext.validUntil))) {
              console.log('prolongResult.loginContext.validUntil', valResult.loginContext.validUntil)
              valResult.tokenIsValid = false
            }
            // sorting is done within the service, because this will not be called by an effect but directly by a guard.
            else if (
              isDevMode() &&
              !!valResult &&
              !!valResult.loginContext &&
              !!valResult.loginContext.allowedActions
            ) {
              valResult.loginContext.allowedActions = valResult.loginContext.allowedActions.sort()
            }
            return valResult
          })
        )
      }),
      tap((valResult) => console.log('token prolonging result: ', valResult))
    )
    return result
  }
}
